import React, { useState } from 'react';
import {
  Trophy,
  BarChart3,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  ArrowRight,
  LayoutGrid,
  Minus,
  Plus,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface ComparisonItem {
  section: string;
  missing: string[];
  extra: string[];
  incorrect: string[];
}

export interface EvaluationReportProps {
  evaluation: {
    overallScore: number;
    requirementsCoverage: number;
    entityModeling: number;
    relationships: number;
    umlDesign: number;
    codeQuality: number;
    completeness: number;
    scoringAvailable?: boolean;
    timeBreakdown: Record<string, number>;
    comparison: ComparisonItem[];
  };
  problemId: string;
  onRetry: () => void;
  onNextProblem: () => void;
  onBackToCatalog: () => void;
  /**
   * Mark the problem complete when automated scoring isn't available.
   * Persists `isCompleted: true` via the normal progress save.
   */
  onMarkComplete?: () => void;
  /** Whether a mark-complete request is currently in flight. */
  markingComplete?: boolean;
  /** Whether the problem has already been marked complete. */
  isCompleted?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

interface DimensionInfo {
  key: keyof Omit<EvaluationReportProps['evaluation'], 'overallScore' | 'timeBreakdown' | 'comparison'>;
  label: string;
  color: string;
  bgColor: string;
}

const DIMENSIONS: DimensionInfo[] = [
  { key: 'requirementsCoverage', label: 'Requirements Coverage', color: 'text-blue-500', bgColor: 'bg-blue-500' },
  { key: 'entityModeling', label: 'Entity Modeling', color: 'text-emerald-500', bgColor: 'bg-emerald-500' },
  { key: 'relationships', label: 'Relationships', color: 'text-purple-500', bgColor: 'bg-purple-500' },
  { key: 'umlDesign', label: 'UML Design', color: 'text-amber-500', bgColor: 'bg-amber-500' },
  { key: 'codeQuality', label: 'Code Quality', color: 'text-pink-500', bgColor: 'bg-pink-500' },
  { key: 'completeness', label: 'Completeness', color: 'text-cyan-500', bgColor: 'bg-cyan-500' },
];

function getScoreColor(score: number): string {
  if (score >= 7) return 'text-green-500';
  if (score >= 5) return 'text-yellow-500';
  return 'text-red-500';
}

function getScoreBgGradient(score: number): string {
  if (score >= 7) return 'from-green-500 to-emerald-600';
  if (score >= 5) return 'from-yellow-500 to-amber-600';
  return 'from-red-500 to-rose-600';
}

function getScoreRingColor(score: number): string {
  if (score >= 7) return '#22c55e';
  if (score >= 5) return '#eab308';
  return '#ef4444';
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
}

function formatSectionName(section: string): string {
  return section
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Component ──────────────────────────────────────────────────────────────────

const EvaluationReport: React.FC<EvaluationReportProps> = ({
  evaluation,
  problemId,
  onRetry,
  onNextProblem,
  onBackToCatalog,
  onMarkComplete,
  markingComplete = false,
  isCompleted = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const { overallScore, timeBreakdown, comparison } = evaluation;

  // Custom problems with no expected data can't be scored automatically.
  const scoringUnavailable = evaluation.scoringAvailable === false;

  // Progress ring for overall score
  const ringSize = 120;
  const ringStrokeWidth = 8;
  const ringRadius = (ringSize - ringStrokeWidth) / 2;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringProgress = overallScore / 10;
  const ringStrokeDashoffset = ringCircumference * (1 - ringProgress);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* ─── Custom problem: scoring not available ───────────────────────── */}
        {scoringUnavailable && (
          <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-blue-600" />
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Automated scoring isn’t available for custom problems
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md leading-relaxed mb-6">
                Custom problems don’t include a reference design, so there’s nothing to score
                against. Your work is saved — review your design below and mark the problem complete
                when you’re done.
              </p>
              {onMarkComplete && (
                <button
                  onClick={onMarkComplete}
                  disabled={markingComplete || isCompleted}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isCompleted ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Completed
                    </>
                  ) : markingComplete ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" /> Marking…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Mark complete
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ─── Hero Section: Overall Score ─────────────────────────────────── */}
        {!scoringUnavailable && (
          <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
            {/* Background gradient accent */}
            <div
              className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getScoreBgGradient(overallScore)}`}
            />

            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className={`w-6 h-6 ${getScoreColor(overallScore)}`} />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Evaluation Report
                </h1>
              </div>

              {/* Score Ring */}
              <div className="relative mb-4">
                <svg
                  width={ringSize}
                  height={ringSize}
                  className="-rotate-90"
                  aria-hidden="true"
                >
                  {/* Background ring */}
                  <circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={ringRadius}
                    fill="none"
                    stroke="currentColor"
                    className="text-gray-200 dark:text-gray-700"
                    strokeWidth={ringStrokeWidth}
                  />
                  {/* Progress ring */}
                  <circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={ringRadius}
                    fill="none"
                    stroke={getScoreRingColor(overallScore)}
                    strokeWidth={ringStrokeWidth}
                    strokeDasharray={ringCircumference}
                    strokeDashoffset={ringStrokeDashoffset}
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-out"
                  />
                </svg>
                {/* Score number in the center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-3xl font-bold ${getScoreColor(overallScore)}`}>
                    {overallScore.toFixed(1)}
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Overall Score (out of 10)
              </p>
            </div>
          </div>
        )}

        {/* ─── Dimension Score Cards ──────────────────────────────────────── */}
        {!scoringUnavailable && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Dimension Scores
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {DIMENSIONS.map((dim) => {
                const score = evaluation[dim.key] as number;
                const isNotAttempted = score === 0;
                const progressPercent = (score / 10) * 100;

                return (
                  <div
                    key={dim.key}
                    className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 shadow-sm transition-all hover:shadow-md"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {dim.label}
                      </span>
                      <span className={`text-lg font-bold ${getScoreColor(score)}`}>
                        {score.toFixed(1)}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ease-out ${dim.bgColor}`}
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>

                    {/* Not attempted indicator */}
                    {isNotAttempted && (
                      <div className="flex items-center gap-1 mt-2">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          Not attempted
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── Time Breakdown ─────────────────────────────────────────────── */}
        <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Time Breakdown
            </h2>
          </div>

          {Object.keys(timeBreakdown).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(timeBreakdown).map(([section, seconds]) => {
                const totalTime = Object.values(timeBreakdown).reduce((a, b) => a + b, 0);
                const percentage = totalTime > 0 ? (seconds / totalTime) * 100 : 0;

                return (
                  <div
                    key={section}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatSectionName(section)}
                    </span>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100 min-w-[4rem] text-right">
                        {formatSeconds(seconds)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No time data available.
            </p>
          )}

          {/* Total time */}
          {Object.keys(timeBreakdown).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Total Time
              </span>
              <span className="text-sm font-mono font-bold text-gray-900 dark:text-gray-100">
                {formatSeconds(Object.values(timeBreakdown).reduce((a, b) => a + b, 0))}
              </span>
            </div>
          )}
        </div>

        {/* ─── Comparison Section ─────────────────────────────────────────── */}
        <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Comparison with Reference
            </h2>
          </div>

          {comparison.length > 0 ? (
            <div className="space-y-3">
              {comparison.map((item) => {
                const isExpanded = expandedSections[item.section] ?? false;
                const totalIssues =
                  item.missing.length + item.extra.length + item.incorrect.length;

                return (
                  <div
                    key={item.section}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    {/* Header */}
                    <button
                      onClick={() => toggleSection(item.section)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      aria-expanded={isExpanded}
                      aria-controls={`comparison-${item.section}`}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatSectionName(item.section)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {totalIssues > 0 ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                            {totalIssues} {totalIssues === 1 ? 'issue' : 'issues'}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                            Perfect
                          </span>
                        )}
                      </div>
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div
                        id={`comparison-${item.section}`}
                        className="px-4 pb-4 space-y-3"
                      >
                        {/* Missing items */}
                        {item.missing.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Minus className="w-3.5 h-3.5 text-red-500" />
                              <span className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
                                Missing ({item.missing.length})
                              </span>
                            </div>
                            <ul className="space-y-1 pl-5">
                              {item.missing.map((m, idx) => (
                                <li
                                  key={idx}
                                  className="text-sm text-gray-700 dark:text-gray-300 list-disc"
                                >
                                  {m}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Extra items */}
                        {item.extra.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Plus className="w-3.5 h-3.5 text-amber-500" />
                              <span className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                                Extra ({item.extra.length})
                              </span>
                            </div>
                            <ul className="space-y-1 pl-5">
                              {item.extra.map((e, idx) => (
                                <li
                                  key={idx}
                                  className="text-sm text-gray-700 dark:text-gray-300 list-disc"
                                >
                                  {e}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Incorrect items */}
                        {item.incorrect.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                              <span className="text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">
                                Incorrect ({item.incorrect.length})
                              </span>
                            </div>
                            <ul className="space-y-1 pl-5">
                              {item.incorrect.map((i, idx) => (
                                <li
                                  key={idx}
                                  className="text-sm text-gray-700 dark:text-gray-300 list-disc"
                                >
                                  {i}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* All clean */}
                        {totalIssues === 0 && (
                          <p className="text-sm text-green-600 dark:text-green-400 italic">
                            Everything matches the reference solution.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No comparison data available.
            </p>
          )}
        </div>

        {/* ─── Navigation Actions ─────────────────────────────────────────── */}
        <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={onRetry}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Problem
            </button>

            <button
              onClick={onNextProblem}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-primary-500 to-blue-600 text-white hover:from-primary-600 hover:to-blue-700 transition-all font-medium text-sm shadow-sm"
            >
              Next Problem
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={onBackToCatalog}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
            >
              <LayoutGrid className="w-4 h-4" />
              Back to Catalog
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationReport;
